import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, Collapse, Typography } from "@mui/material";
import CustomTabs from "../../common/custom-tabs";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import {
  INSURANCE_HEALTH_UPLOAD_ENDPOINT,
  INSURANCE_GENERAL_UPLOAD_ENDPOINT,
  INSURANCE_LIFE_UPLOAD_ENDPOINT,
  INSURANCE_UPLOAD_LOGS_ENDPOINT,
} from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

const INSURANCE_TYPE_TABS = [
  { value: "health", label: "Health" },
  { value: "general", label: "General" },
  { value: "life", label: "Life" },
];

const UPLOAD_ENDPOINT_BY_TYPE = {
  health: INSURANCE_HEALTH_UPLOAD_ENDPOINT,
  general: INSURANCE_GENERAL_UPLOAD_ENDPOINT,
  life: INSURANCE_LIFE_UPLOAD_ENDPOINT,
};

const REQUIRED_COLUMNS_HELP = {
  health: "Required: Policy No, Name, Mobile Number (10-digit Indian number) · Optional: Expiry Date, Policy, PREMIUM",
  general: "Required: NAME, POLICY NUMBER, CONTACT NO & MAIL ID (mobile/email) · Optional: INSURER, POLICY TYPE, EXPIRY DATE",
  life: "Required: Name, Policy Number, Contact Number (10-digit Indian number) · Optional: Age, Due Date, PREMIUM AMOUNT, Sum Assured, PLAN, Insurer, Frequency",
};

const LOG_COLUMNS = [
  { id: "insurance_type", label: "Type", minWidth: 100, render: (row) => row.insurance_type?.charAt(0).toUpperCase() + row.insurance_type?.slice(1) },
  { id: "file_name", label: "File Name", minWidth: 200 },
  { id: "uploaded_by_name", label: "Uploaded By", minWidth: 150 },
  {
    id: "created_at",
    label: "Uploaded At",
    minWidth: 170,
    render: (row) => formatWithTzLabel(row.created_at),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 100,
    render: (row) =>
      row.status === "success" ? (
        <Chip variant="outlined" color="success" label="Success" size="small" />
      ) : (
        <Chip variant="outlined" color="error" label="Failed" size="small" />
      ),
  },
  { id: "message", label: "Message", minWidth: 260 },
];

const InsuranceUploadTab = () => {
  const { getValidToken } = useAuth();

  const [insuranceType, setInsuranceType] = useState("health");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (!alertOpen) return;
    const id = setTimeout(() => setAlertOpen(false), 8000);
    return () => clearTimeout(id);
  }, [alertOpen]);

  const showAlert = (severity, message) => {
    setAlertStatus(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const params = new URLSearchParams({ limit: 10, offset: 0, insurance_type: `is:${insuranceType}` });
      const res = await fetch(`${INSURANCE_UPLOAD_LOGS_ENDPOINT}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch upload logs");
      const data = await res.json();
      setLogs(data.results ?? []);
    } catch (err) {
      console.error("Error fetching insurance upload logs:", err);
    } finally {
      setLogsLoading(false);
    }
  }, [getValidToken, insuranceType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] ?? null);
    setResult(null);
    setAlertOpen(false);
  };

  const resetFileInput = () => {
    setFile(null);
    const el = document.getElementById("insurance-file-input");
    if (el) el.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      showAlert("error", "Please select a file to upload.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(UPLOAD_ENDPOINT_BY_TYPE[insuranceType], {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        showAlert("error", data.error || "Upload failed.");
        return;
      }

      setResult(data);
      const hasErrors = data.errors?.length > 0;
      const hasSuccess = data.inserted > 0 || data.updated > 0;

      if (!hasErrors) {
        showAlert("success", `Upload complete: ${data.inserted} inserted, ${data.updated} updated.`);
      } else if (hasSuccess) {
        showAlert(
          "warning",
          `Partial success: ${data.inserted} inserted, ${data.updated} updated. ${data.failed} row(s) failed.`,
        );
      } else {
        showAlert("error", `Upload failed — ${data.failed} row(s) had errors.`);
      }

      resetFileInput();
      fetchLogs();
    } catch (err) {
      console.error("Upload error:", err);
      showAlert("error", "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ ...typography.h2, mb: 1 }}>Insurance Type</Typography>
        <CustomTabs
          value={insuranceType}
          onChange={(_, val) => {
            setInsuranceType(val);
            setResult(null);
          }}
          tabs={INSURANCE_TYPE_TABS}
          sx={{ maxWidth: 320 }}
        />
      </Box>

      <Box sx={{ mb: 3, width: "100%" }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {REQUIRED_COLUMNS_HELP[insuranceType]}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>Select a file to upload:</Typography>
        <input id="insurance-file-input" type="file" accept=".csv" onChange={handleFileChange} />
      </Box>

      <Collapse in={alertOpen} sx={{ mb: alertOpen ? 1.5 : 0, width: "100%", maxWidth: 600 }}>
        <Alert severity={alertStatus} onClose={() => setAlertOpen(false)}>
          {alertMessage}
        </Alert>
      </Collapse>

      <ButtonOne
        title={loading ? "Uploading…" : "Upload"}
        variant="contained"
        onClick={handleUpload}
        disabled={loading || !file}
      />

      {result && (
        <Box sx={{ mt: 3, width: "100%", maxWidth: 600 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Upload Summary
          </Typography>
          <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1, overflow: "hidden" }}>
            {[
              { label: "Total Records", value: result.total ?? "—" },
              { label: "Inserted", value: result.inserted ?? "—", color: "success.main" },
              { label: "Updated", value: result.updated ?? "—", color: "info.main" },
              { label: "Failed", value: result.failed ?? "—", color: result.failed > 0 ? "error.main" : "text.primary" },
              { label: "Skipped", value: result.skipped ?? "—" },
            ].map(({ label, value, color }, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  bgcolor: i % 2 === 0 ? "#F9FAFB" : "#FFFFFF",
                }}
              >
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} color={color || "text.primary"}>
                  {typeof value === "number" ? value.toLocaleString() : value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {result?.errors?.length > 0 && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: 600 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Failure Details ({result.errors.length})
          </Typography>
          <Box sx={{ maxHeight: 280, overflowY: "auto", border: "1px solid #E5E7EB", borderRadius: 1, p: 1.5 }}>
            {result.errors.map(({ row, errors }) => (
              <Box
                key={row}
                sx={{ mb: 1, pb: 1, borderBottom: "1px solid #F3F4F6", "&:last-child": { mb: 0, pb: 0, borderBottom: "none" } }}
              >
                <Typography variant="caption" fontWeight={700} color="error.main">
                  Row {row}
                </Typography>
                {errors.map((msg, i) => (
                  <Typography key={i} variant="caption" display="block" color="text.secondary">
                    &bull; {msg}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 4, width: "100%" }}>
        <Typography sx={{ ...typography.h2, mb: 1 }}>Recent Uploads</Typography>
        <AppTable
          columns={LOG_COLUMNS}
          rows={logs}
          rowKey="id"
          loading={logsLoading}
          emptyMessage="No uploads yet"
          maxHeight={320}
          hideMainScroll={false}
          containerSx={{ overflowX: "auto", width: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default InsuranceUploadTab;
